// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {CurrencyInput, Icon, Space} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {Form, useResponsive, useToast, useModal} from '@supermove/hooks';
import {PaymentModel} from '@supermove/models';
import {Currency} from '@supermove/utils';

// App
import Drawer from '@shared/design/components/Drawer';
import FieldInput from '@shared/design/components/Field/FieldInput';
import FieldValue from '@shared/design/components/Field/FieldValue';
import ActionModal from '@shared/design/components/Modal/SmallModal/ActionModal';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import PaymentMethodKind from '@shared/modules/PaymentMethod/enums/PaymentMethodKind';
import RefundPaymentForm, {
  RefundPaymentFormToFormType,
} from '@shared/modules/Refund/forms/RefundPaymentForm';
import useRefundPaymentMutation from '@shared/modules/Refund/hooks/useRefundPaymentMutation';

const PaymentFields = ({
  form,
  field,
  payment,
}: {
  form: Form<{refundPaymentForm: RefundPaymentFormToFormType}>;
  field: string;
  payment: PaymentModel;
}) => {
  return (
    <React.Fragment>
      <FieldValue label={'Payment Name'} value={payment.name} isResponsive />
      <Space height={16} />
      <FieldValue
        label={'Payment Method'}
        value={payment.paymentMethod?.name || PaymentMethodKind.getName(payment.method)}
        isResponsive
      />
      <Space height={16} />
      {payment.method === PaymentMethodKind.PAYENGINE_CREDIT_CARD && (
        <React.Fragment>
          <FieldValue label={'Refund to'} value={payment.displayCardName} />
          <Space height={16} />
        </React.Fragment>
      )}
      <FieldInput
        {...form}
        name={`${field}.name`}
        label={'Refund Name'}
        isResponsive
        isRequired
        input={{
          placeholder: 'Enter refund name',
        }}
      />
      <Space height={16} />
      <FieldInput
        {...form}
        name={`${field}.amount`}
        label={'Refund Amount'}
        component={CurrencyInput}
        isResponsive
        isRequired
        tooltip={
          'The maximum amount that can be refunded is the total amount of the original payment.'
        }
        handleBlur={(e) => {
          const amount = Currency.convertToCents(e.currentTarget.value);
          form.setFieldValue(`${field}.amount`, amount);
        }}
        input={{
          component: FieldInput.TextInput,
          placeholder: 'Enter a refund amount',
          setFieldValue: form.setFieldValue,
          setFieldTouched: form.setFieldTouched,
        }}
      />
      <Space height={16} />
      <FieldInput
        {...form}
        name={`${field}.description`}
        label={'Refund Description'}
        isResponsive
        input={{
          placeholder: 'Enter a refund description',
          style: {height: 80, paddingTop: 10},
          multiline: true,
        }}
      />
    </React.Fragment>
  );
};

const RefundPaymentDrawerContent = ({
  handleClose,
  refetch,
  payment,
}: {
  handleClose: () => void;
  refetch: () => void;
  payment: PaymentModel;
}) => {
  const responsive = useResponsive();
  const refundPaymentForm = RefundPaymentForm.new({
    paymentId: _.toNumber(payment.id),
  });
  const refundSuccessToast = useToast({
    ToastComponent: SuccessToast,
    message: 'Refund issued.',
  });
  const cardActionModal = useModal({name: 'Credit Card Refund Modal'});

  const {form, submitting, handleSubmit} = useRefundPaymentMutation({
    refundPaymentForm,
    onSuccess: () => {
      refundSuccessToast.handleToast();
      refetch();
      handleClose();
    },
    onError: (errors: unknown) => {
      console.log({errors});
    },
  });
  return (
    <React.Fragment>
      <Drawer.Body>
        <PaymentFields form={form} field={'refundPaymentForm'} payment={payment} />
      </Drawer.Body>
      {responsive.desktop ? (
        <Drawer.Footer
          isSubmitting={submitting}
          primaryAction={
            payment.method === PaymentMethodKind.PAYENGINE_CREDIT_CARD
              ? cardActionModal.handleOpen
              : handleSubmit
          }
          secondaryAction={handleClose}
          primaryActionIcon={Icon.Check}
          primaryActionText={'Save'}
          secondaryActionText={'Cancel'}
        />
      ) : (
        <Drawer.FooterMobile
          isSubmitting={submitting}
          primaryAction={
            payment.method === PaymentMethodKind.PAYENGINE_CREDIT_CARD
              ? cardActionModal.handleOpen
              : handleSubmit
          }
          primaryActionText={'Save'}
        />
      )}
      <ActionModal
        title={'Refund to credit card?'}
        message={
          `A refund of ${_.get(form.values, 'refundPaymentForm.amount')} will be issued to the ` +
          `original payment method (${payment.displayCardName}). ` +
          `Please allow 7–10 business days for processing.`
        }
        isOpen={cardActionModal.isOpen}
        handlePressOutside={cardActionModal.handleClose}
        handlePrimaryAction={handleSubmit}
        handleSecondaryAction={cardActionModal.handleClose}
        primaryActionText={'Confirm'}
        secondaryActionText={'Cancel'}
        icon={Icon.CirclePlus}
        isSubmitting={submitting}
        isDisabled={submitting}
      />
    </React.Fragment>
  );
};

const RefundPaymentDrawer = ({
  handleClose,
  isOpen,
  refetch,
  payment,
}: {
  handleClose: () => void;
  isOpen: boolean;
  refetch: () => void;
  payment: PaymentModel;
}) => {
  const responsive = useResponsive();
  return (
    <Drawer isOpen={isOpen} width={Drawer.WIDTH.DEFAULT}>
      <Drawer.Header
        isResponsive
        headerText={
          payment.method === PaymentMethodKind.PAYENGINE_CREDIT_CARD
            ? `Issue Credit Card Refund`
            : `Record Refund`
        }
        handleClose={handleClose}
        isClosable={!responsive.desktop}
      />
      <RefundPaymentDrawerContent handleClose={handleClose} refetch={refetch} payment={payment} />
    </Drawer>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
RefundPaymentDrawer.fragment = gql`
  fragment RefundPaymentDrawer on Payment {
    id
    name
    method
    displayCardName
    paymentMethod {
      id
      name
    }
  }
`;

export default RefundPaymentDrawer;
